import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaShieldAlt, FaGlobe, FaExchangeAlt,
  FaRocket, FaPhone
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { useLanguage } from '../../../contexts/LanguageContext'; // Ajout de l'import

const CyberSecurity = () => {
  const { t } = useLanguage(); // Initialisation des traductions
  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [dbProducts, setDbProducts] = useState([]);

  useEffect(() => {
    const fetchNewProducts = async () => {
      try {
        const response = await api.get('/public/products?keyword=Sécurité');
        setDbProducts(response.data);
      } catch (error) { console.error('Erreur API:', error); }
    };
    fetchNewProducts();
  }, []);

  const scrollToOverview = () => {
    document.getElementById('solutions-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div>
      {/* ── HERO ── */}
      <section ref={setHeroRef} className="hero-section-sii" style={{ background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.92) 0%, rgba(71, 85, 105, 0.92) 100%)', minHeight: '70vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={7}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <div className="mb-4">
                  <span className="badge-sii"><FaShieldAlt className="me-2" />{t.cybersecurity.hero.badge}</span>
                </div>
                <h1 className="display-3 fw-bold mb-4 text-white" style={{ fontSize: 'clamp(2rem, 5vw, 3.2rem)', lineHeight: '1.1' }}
                  dangerouslySetInnerHTML={{ __html: t.cybersecurity.hero.title }} />
                <p className="lead text-light mb-4">
                  {t.cybersecurity.hero.subtitle}
                </p>
                <div className="d-flex flex-wrap gap-3 mb-4">
                  <Button size="lg" onClick={scrollToOverview} style={{ borderRadius: '16px', padding: '1rem 2.2rem', fontWeight: 600, background: '#d90000', border: 'none', boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)' }}>
                    <FaRocket className="me-2" />{t.cybersecurity.hero.btnExplore}
                  </Button>
                  <Button variant="outline-light" size="lg" href="tel:+21670839470" style={{ borderRadius: '16px', padding: '1rem 2.2rem', fontWeight: 600, borderWidth: 2 }}>
                    {t.cybersecurity.hero.btnContact}
                  </Button>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── CONTENT ── */}
      <section className="py-5" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }}>
        <Container>
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-4" style={{ fontSize: '2.5rem', color: '#1e293b' }}
              dangerouslySetInnerHTML={{ __html: t.cybersecurity.intro.title }} />
            <p className="text-muted" style={{ fontSize: '1.1rem', maxWidth: '800px', margin: '0 auto' }}>
              {t.cybersecurity.intro.subtitle}
            </p>
          </div>
        </Container>
      </section>

      {/* ── SOLUTIONS ── */}
      <section id="solutions-anchor" className="security-solutions-section-sii" style={{ padding: '6rem 0', background: '#1e293b', position: 'relative' }}>
        <Container>
          <div className="text-center mb-5">
            <h2 className="text-white fw-bold" dangerouslySetInnerHTML={{ __html: t.cybersecurity.solutions.title }} />
            <p className="text-light opacity-75">{t.cybersecurity.solutions.subtitle}</p>
          </div>

          <Row className="g-4">
            {/* Piliers statiques */}
            {[
              {
                icon: <FaShieldAlt size={40} color="#d90000" />,
                title: t.loadbalancer.security.pillars.fw.title,
                desc: t.loadbalancer.security.pillars.fw.desc,
              },
              {
                icon: <FaGlobe size={40} color="#d90000" />,
                title: t.loadbalancer.security.pillars.waf.title,
                desc: t.loadbalancer.security.pillars.waf.desc,
              },
              {
                icon: <FaExchangeAlt size={40} color="#d90000" />,
                title: t.loadbalancer.security.pillars.cyber.title,
                desc: t.loadbalancer.security.pillars.cyber.desc,
              },
            ].map((item, i) => (
              <Col key={i} lg={4} md={6}>
                <Card className="h-100 p-4 border-0 shadow-sm" style={{ borderRadius: '20px' }}>
                  <div className="mb-3">{item.icon}</div>
                  <h4 className="fw-bold">{item.title}</h4>
                  <p className="small text-muted">{item.desc}</p>
                </Card>
              </Col>
            ))}

            {/* Produits dynamiques */}
            {dbProducts.map((product) => (
              <Col key={product.id} lg={4} md={6}>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <Card className="h-100 p-4 border-0 shadow-sm" style={{ borderRadius: '20px', borderTop: '4px solid #d90000' }}>
                    <div className="text-center mb-3">
                      <img src={product.image_url} alt={product.title} style={{ height: '80px', objectFit: 'contain' }} />
                    </div>
                    <h4 className="fw-bold">{product.title}</h4>
                    <p className="small text-muted">{product.description}</p>
                    <div className="mt-auto"><Badge bg="danger">{t.cybersecurity.solutions.newBadge}</Badge></div>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── CTA ── */}
      <section className="py-5 bg-light">
        <Container className="text-center">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}>
            <h3 className="fw-bold mb-4">{t.cybersecurity.cta.title}</h3>
            <Button variant="danger" size="lg" style={{ borderRadius: '12px' }} onClick={() => window.location.href = '/contact'}>
              <FaPhone className="me-2" />{t.cybersecurity.cta.btn}
            </Button>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default CyberSecurity;